import { useState, useEffect, useRef } from 'react';
import adminApi from '@/lib/adminApi';
import { toast } from 'sonner';
import { Globe, Image, Type, Save, Loader2, Upload, Smartphone, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';


export const SiteSettingsTab = ({ onRefresh }) => {
  const [settings, setSettings] = useState({
    favicon_url: '',
    tagline: '',
    og_image: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconStatus, setIconStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchIconStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await adminApi.get(`/site-settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIconStatus = async () => {
    try {
      const res = await adminApi.get(`/site-settings/app-icon/status`);
      setIconStatus(res.data);
    } catch (error) {
      console.error('Error fetching icon status:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/site-settings`, settings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Configuración guardada exitosamente');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Error al guardar la configuración');
      console.error('Error saving site settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Usa PNG, JPG o WebP');
      return;
    }

    setUploadingIcon(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await adminApi.post('/site-settings/app-icon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        toast.success(res.data.message);
        fetchIconStatus();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al subir el ícono';
      toast.error(msg);
    } finally {
      setUploadingIcon(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="text-[#E91E8C]" size={24} />
          <h2 className="text-xl font-bold text-black">Configuración del Sitio</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E91E8C] hover:bg-[#E91E8C]/80 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin mr-2" size={16} />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>

      {/* App Icon Upload Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Smartphone className="text-[#E91E8C]" size={20} />
          <h3 className="font-bold text-black">Ícono de la App y Favicon</h3>
        </div>
        
        <p className="text-sm text-gray-600">
          Sube una imagen PNG o JPG (recomendado 512x512 px o mayor). Se generarán automáticamente:
        </p>
        
        <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
          <li>Favicon para el navegador web</li>
          <li>Íconos para PWA (app instalable)</li>
          <li>Ícono para iOS (Apple Touch Icon)</li>
        </ul>

        <div className="flex items-center gap-4">
          {/* Current Icon Preview */}
          {iconStatus?.base_icon_url && (
            <div className="flex-shrink-0">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${iconStatus.base_icon_url}`}
                alt="Ícono actual"
                className="w-20 h-20 rounded-xl border-2 border-gray-200 object-cover"
              />
              <p className="text-xs text-gray-500 text-center mt-1">Actual</p>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleIconUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingIcon}
              variant="outline"
              className="w-full border-2 border-dashed border-gray-300 hover:border-[#E91E8C] py-8"
            >
              {uploadingIcon ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={20} />
                  Generando íconos...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={20} />
                  Subir nueva imagen
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Icon Status */}
        {iconStatus && (
          <div className="flex flex-wrap gap-2 pt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${iconStatus.favicon ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {iconStatus.favicon && <Check size={12} />} Favicon
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${iconStatus.apple_touch_icon ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {iconStatus.apple_touch_icon && <Check size={12} />} iOS Icon
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${iconStatus.pwa_icons?.['512x512'] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {iconStatus.pwa_icons?.['512x512'] && <Check size={12} />} PWA Icons
            </span>
          </div>
        )}

        {iconStatus?.has_custom_icon && (
          <p className="text-xs text-green-600 mt-2">
            ✓ Ícono personalizado guardado en la nube
          </p>
        )}
      </div>

      {/* Other Settings Form */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        {/* Tagline */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Type size={16} />
            Descripción del Sitio
          </label>
          <Input
            type="text"
            value={settings.tagline}
            onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
            placeholder="Ej: Contenido original - Shows, Música, Juegos y más"
            className="bg-gray-50 border-gray-200"
          />
          <p className="text-xs text-gray-500">
            Este texto aparece al compartir el sitio en redes sociales (Facebook, Twitter, WhatsApp, etc.)
          </p>
        </div>

        {/* OG Image */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Image size={16} />
            Imagen para Redes Sociales (OG Image)
          </label>
          <Input
            type="url"
            value={settings.og_image}
            onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
            placeholder="https://ejemplo.com/og-image.jpg"
            className="bg-gray-50 border-gray-200"
          />
          <p className="text-xs text-gray-500">
            Imagen que se muestra al compartir el sitio en redes sociales (Facebook, Twitter, etc.). Tamaño recomendado: 1200x630 px
          </p>
          {settings.og_image && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 block mb-2">Vista previa:</span>
              <img 
                src={settings.og_image} 
                alt="OG Image preview" 
                className="max-w-full h-auto max-h-48 rounded border border-gray-200"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          <strong>Nota:</strong> Los cambios en el favicon pueden tardar unos minutos en reflejarse debido al caché del navegador. 
          Prueba recargando la página con Ctrl+F5 (o Cmd+Shift+R en Mac).
        </p>
      </div>
    </div>
  );
};
